/**
 * The System Library's adapter for the shared <LibraryDocumentsView scope>
 * (Library-PR-X3 / Phase 4) — the sysadmin scope.
 *
 * It wires the shared list to the /admin/platform/library/* routes and the
 * publish lifecycle. It lived in LibraryDocumentsView.adapters.tsx beside the
 * tenant adapter until Library LB6-T2, which made the TENANT list the
 * server's answer (one GET /knowledge/documents with every filter, the order
 * and the counts as the server's). The System Library's routes take no
 * search, kind, sort or page: this adapter still merges its two lists and
 * filters, orders and counts them in the browser. That is a recorded debt of
 * the Sysadmin rail — scripts/ci/no-web-derivation-lint.mjs
 * LIBRARY_DERIVATION_PENDING names this file — not a pattern: the entry fails
 * the run the day the routes answer and this file stops deriving.
 */
import { useMemo } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { ArticleStateSchema, DocumentKindSchema, type DocumentKind } from '@argusai/shared';
import { getApiErrorMessage } from '@/lib/api-error';
import { LibraryStatusPill } from '@/components/sysadmin/library-lifecycle';
import {
  useSysLibArticles,
  useSysLibFolders,
  useDeleteSysLibArticle,
  usePublishSysLibArticle,
  useSysLibDocuments,
  useDeleteSysLibDocument,
  usePublishSysLibDocument,
  useBulkPublishSysLib,
  type LifecycleStatus,
  type SysLibArticle,
  type SysLibDocument,
} from '@/lib/use-sysadmin-library';
import { formatBytes } from './LibraryDocumentsView.helpers';
import { ARTICLE_STATE_LABEL } from './document-kind';
import type {
  BulkActionDescriptor,
  DocCounts,
  FilterOption,
  LibraryDocumentsAdapter,
  LibraryDocumentsAllows,
  LibraryListSort,
  ListDocsQuery,
  ListDocsResult,
  NormalizedLibraryDoc,
} from './LibraryDocumentsView.types';

const SYSADMIN_ALLOWS: LibraryDocumentsAllows = Object.freeze({
  bulkSelect: true,
  upload: true,
  writeArticle: true,
  edit: () => true,
  publish: () => true,
  delete: () => true,
});

/** A System Library document is an uploaded file or an article — platform documents are never synced. */
const SYSADMIN_KINDS: readonly DocumentKind[] = Object.freeze(
  DocumentKindSchema.options.filter((k) => k !== 'synced'),
);

/** The lifecycle filter: the one article state alphabet (ArticleStateSchema), which both lists speak, with its one set of labels. */
const SYSADMIN_STATUS_OPTIONS: readonly FilterOption[] = Object.freeze(
  ArticleStateSchema.options.map((s) => ({ value: s, label: ARTICLE_STATE_LABEL[s] })),
);

/** The columns this adapter orders by (in the browser — see the file header). */
const SYSADMIN_SORT_KEYS = Object.freeze(['title', 'kind', 'status', 'updatedAt'] as const);
const SYSADMIN_DEFAULT_SORT: LibraryListSort = Object.freeze({ key: 'title', direction: 'asc' });

export interface SysadminAdapterCallbacks {
  /** Open the SystemArticleEditorDialog with `article=null` (create). */
  onOpenCreateArticle: () => void;
  /** Open the SystemArticleEditorDialog with the given article (edit). */
  onEditArticle: (article: SysLibArticle) => void;
  /** Open the document-metadata edit dialog. */
  onEditDocument: (doc: SysLibDocument) => void;
  /** Open the upload dialog. */
  onOpenUpload: () => void;
  /** Open the unpublish dialog for an article. */
  onUnpublishArticle: (article: SysLibArticle) => void;
  /** Open the unpublish dialog for a document. */
  onUnpublishDocument: (doc: SysLibDocument) => void;
}

/**
 * Hook factory — returns a sysadmin adapter wired to the given dialog
 * callbacks. Called once per render in the route file; useMemo'd inside.
 *
 * Per frozen-object invariant (PR-B audit): the returned adapter is
 * `Object.freeze`'d before exposing to the shared body so accidental
 * mutation is caught in dev.
 */
export function useSysadminAdapter(callbacks: SysadminAdapterCallbacks): LibraryDocumentsAdapter {
  const publishArticleMut = usePublishSysLibArticle();
  const deleteArticleMut = useDeleteSysLibArticle();
  const publishDocumentMut = usePublishSysLibDocument();
  const deleteDocumentMut = useDeleteSysLibDocument();
  const bulkMut = useBulkPublishSysLib();

  // The adapter is constructed once per render — useMemo prevents the
  // shared View from re-mounting state when the route re-renders due
  // to unrelated cache updates.
  return useMemo<LibraryDocumentsAdapter>(() => {
    const adapter: LibraryDocumentsAdapter = {
      // ── What the operator may use ──
      // The System Library screens and their /admin/platform routes are the
      // platform operator's own (requireSysadmin); the page is not reachable
      // without that grant, so every list control it draws is usable.
      allows: SYSADMIN_ALLOWS,

      // ── Page chrome ──
      pageTitle: 'All System Documents',
      buildStatsLine: (counts: DocCounts) =>
        `${counts.total} items · ${counts.byKind.article} articles · ${counts.byKind.file} uploaded`,
      emptyStateMessage:
        'No system documents yet. Upload a file or write an article to start.',
      emptyFilterMessage: 'No documents match your filters.',
      uploadDialogTitle: 'Upload System Document',
      uploadDialogDescription:
        'Upload a file as a system Library document (organization_id = NULL). Visible to no tenant until you publish it. Same RAG-indexed / stored-only format matrix as tenant uploads.',

      // ── Affordance flags ──
      showSidebar: false,
      showStarColumn: false,
      showPublishLifecycle: true,
      showAuthorColumn: false, // sysadmin always shows "Platform Admin", omit column
      bulkAction: 'publish-only',
      supportedKinds: SYSADMIN_KINDS,
      statusOptions: SYSADMIN_STATUS_OPTIONS,
      // Platform documents have no tenant tag door (decision 60): no Tag filter.
      tagFilter: false,
      sortKeys: SYSADMIN_SORT_KEYS,
      defaultSort: SYSADMIN_DEFAULT_SORT,

      // ── Data ──
      useList: (query) => useSysadminList(query),

      // ── Row event handlers ──
      onEditRow: (doc) => {
        if (doc.kind === 'article') {
          callbacks.onEditArticle(doc.raw as SysLibArticle);
        } else if (doc.kind === 'file') {
          callbacks.onEditDocument(doc.raw as SysLibDocument);
        }
      },
      onDeleteRow: (doc) => {
        // #677 — Confirmation moved to the shared View's AlertDialog
        // (Pod-DDD misc cluster). The View only fires onDeleteRow after
        // the user has confirmed in the in-app dialog, so the previous
        // native browser confirm here would double-prompt.
        if (doc.kind === 'article') {
          deleteArticleMut.mutate({ id: doc.id }, {
            onSuccess: () => toast.success('Deleted'),
            onError: (e: unknown) => toast.error(getApiErrorMessage(e, 'Delete failed')),
          });
        } else {
          deleteDocumentMut.mutate({ id: doc.id }, {
            onSuccess: () => toast.success('Deleted'),
            onError: (e: unknown) => toast.error(getApiErrorMessage(e, 'Delete failed')),
          });
        }
      },
      onPublishRow: (doc) => {
        if (doc.kind === 'article') {
          publishArticleMut.mutate({ id: doc.id }, {
            onSuccess: () => toast.success(`Published "${doc.title}"`),
            onError: (e: unknown) => toast.error(getApiErrorMessage(e, 'Publish failed')),
          });
        } else {
          publishDocumentMut.mutate({ id: doc.id }, {
            onSuccess: () => toast.success(`Published "${doc.title}"`),
            onError: (e: unknown) => toast.error(getApiErrorMessage(e, 'Publish failed')),
          });
        }
      },
      onUnpublishRow: (doc) => {
        if (doc.kind === 'article') {
          callbacks.onUnpublishArticle(doc.raw as SysLibArticle);
        } else {
          callbacks.onUnpublishDocument(doc.raw as SysLibDocument);
        }
      },

      // ── Share — N/A in sysadmin (no star column: showStarColumn false) ──
      onShareRow: undefined,
      onMoveRow: undefined,

      // ── New-dropdown handlers ──
      onOpenUpload: callbacks.onOpenUpload,
      onOpenCreateArticle: callbacks.onOpenCreateArticle,

      // ── Bulk actions (multi-action) ──
      isSelectable: () => true,
      getBulkActions: ({ selectedIds, selectedDocs, selectedKinds }) => {
        const mixed = selectedKinds.size > 1;
        void selectedDocs; // current sysadmin endpoints use the id set only
        const action: BulkActionDescriptor = {
          id: 'publish',
          label: 'Publish all',
          icon: Send,
          title: mixed
            ? 'Bulk publish supports one kind at a time. Filter by Type first.'
            : 'Publish the selected items',
          busy: bulkMut.isPending,
          disabled: mixed,
          disabledReason: mixed
            ? 'Mixed kinds — filter by Type to enable bulk publish'
            : undefined,
          onClick: () => {
            const kind = selectedKinds.values().next().value;
            if (!kind) return;
            const entity = kind === 'article' ? 'article' : 'document';
            bulkMut.mutate(
              { entity, ids: Array.from(selectedIds) },
              {
                onSuccess: (r) =>
                  toast.success(
                    `Published ${r.published} ${kind === 'article' ? 'articles' : 'documents'}`,
                  ),
                onError: (e: unknown) => toast.error(getApiErrorMessage(e, 'Bulk publish failed')),
              },
            );
          },
        };
        return [action];
      },

      // ── Custom cells ──
      renderStatusCell: (doc) => {
        return <LibraryStatusPill status={doc.status as LifecycleStatus} />;
      },
      renderTitleSubline: (doc) => {
        if (doc.kind === 'file') {
          const parts: string[] = [];
          if (doc.fileName) parts.push(doc.fileName);
          if (doc.fileSize !== undefined) parts.push(formatBytes(doc.fileSize));
          if (doc.ingestStatus && doc.ingestStatus !== 'ready') parts.push(doc.ingestStatus);
          return parts.join(' · ');
        }
        return null;
      },
    };
    // Frozen-object invariant — back-ported from PR-B audit.
    return Object.freeze(adapter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callbacks.onOpenCreateArticle,
    callbacks.onEditArticle,
    callbacks.onEditDocument,
    callbacks.onOpenUpload,
    callbacks.onUnpublishArticle,
    callbacks.onUnpublishDocument,
    bulkMut.isPending,
  ]);
}

// ─── Internal helper: sysadmin list query ──────────────────────────────────

/** Order two rows by one column (the sysadmin list's in-browser order — see the file header). */
function compareRows(a: NormalizedLibraryDoc, b: NormalizedLibraryDoc, sort: LibraryListSort): number {
  const sign = sort.direction === 'asc' ? 1 : -1;
  if (sort.key === 'updatedAt') {
    return sign * (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
  }
  const pick = (d: NormalizedLibraryDoc): string =>
    sort.key === 'kind' ? d.kind : sort.key === 'status' ? d.status : d.title;
  return sign * pick(a).localeCompare(pick(b));
}

function useSysadminList(query: ListDocsQuery): ListDocsResult {
  const status = query.statusFilter === 'all' ? undefined : (query.statusFilter as LifecycleStatus);
  const folderId =
    query.folderFilter === 'all' || query.folderFilter === '__root__' ? undefined : query.folderFilter;

  const articlesQ = useSysLibArticles({ status, folderId });
  const documentsQ = useSysLibDocuments({ publishStatus: status, folderId });
  // Decision 18 — the System Library has ONE folder set; the folder filter
  // and every row's folder name read it once (L18-11).
  const foldersQ = useSysLibFolders();

  const folderNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of foldersQ.data?.items ?? []) m.set(f.id, f.name);
    return m;
  }, [foldersQ.data]);

  const docs = useMemo<NormalizedLibraryDoc[]>(() => {
    const out: NormalizedLibraryDoc[] = [];
    if (query.kindFilter === 'all' || query.kindFilter === 'article') {
      for (const a of articlesQ.data?.items ?? []) {
        out.push({
          id: a.id,
          title: a.title,
          kind: 'article',
          status: a.status,
          ingestStatus: null,
          authorLabel: 'Platform Admin',
          updatedAt: a.updatedAt,
          folderId: a.folderId,
          folderName: a.folderId ? folderNameMap.get(a.folderId) ?? '—' : '',
          raw: a,
        });
      }
    }
    // A System Library document is an uploaded file (platform documents are
    // never connector-synced); the kind vocabulary is the Document kind.
    if (query.kindFilter === 'all' || query.kindFilter === 'file') {
      for (const d of documentsQ.data?.items ?? []) {
        out.push({
          id: d.id,
          title: d.title,
          kind: 'file',
          status: d.publishStatus,
          ingestStatus: d.status,
          authorLabel: 'Platform Admin',
          updatedAt: d.updatedAt,
          folderId: d.folderId,
          folderName: d.folderId ? folderNameMap.get(d.folderId) ?? '—' : '',
          fileName: d.fileName,
          fileSize: d.fileSize,
          raw: d,
        });
      }
    }
    const text = query.search.trim().toLowerCase();
    const matched = text
      ? out.filter((d) => d.title.toLowerCase().includes(text) || (d.fileName?.toLowerCase() ?? '').includes(text))
      : out;
    return [...matched].sort((a, b) => compareRows(a, b, query.sort));
  }, [articlesQ.data, documentsQ.data, folderNameMap, query.kindFilter, query.search, query.sort]);

  const counts = useMemo<DocCounts>(() => {
    const byKind = Object.fromEntries(
      DocumentKindSchema.options.map((k) => [k, docs.filter((d) => d.kind === k).length]),
    ) as Record<DocumentKind, number>;
    return { total: docs.length, byKind };
  }, [docs]);

  const folders = useMemo(
    () => (foldersQ.data?.items ?? []).map((f) => ({ id: f.id, name: f.name })),
    [foldersQ.data],
  );

  const error =
    (foldersQ.error as Error | null) ||
    (articlesQ.error as Error | null) ||
    (documentsQ.error as Error | null) ||
    null;
  return {
    docs,
    counts: error ? null : counts,
    folders,
    // The folder list names every row's folder: until it is read the rows are
    // loading, and a failed read is the list's error, never a '—' folder.
    loading: foldersQ.isLoading || articlesQ.isLoading || documentsQ.isLoading,
    error,
    // The System Library routes answer their whole list: there is no next page.
    hasMore: false,
    loadingMore: false,
    loadMore: () => undefined,
  };
}
